import { computed, observable, action } from 'mobx';
import { OrderRepository } from '../repositories/OrderRepository';
import { FormattedTextValues } from '@estee/elc-formatters';
import { ITranslationsCollection } from '@estee/elc-service';
import { OrderTrackingSubmittedUseCase } from '../use-cases/OrderTrackingSubmittedUseCase';
import { ConfigRepository } from '../repositories/ConfigRepository';
import { ErrorMessageType } from '../utils/Constants';
import { emit, events } from 'elc-service-bus';
import { EncryptedOrderNumberDataType } from '../../api/OrderTrackingApiSdk';

export default class OrderTrackingViewController {
    @observable public isScrolled: boolean = true;
    @observable public isTrackOrderShow: boolean = true;
    @observable public isVerticalTMOProgressBar: boolean;
    // This value is populated after view is loaded
    @observable private translations?: ITranslationsCollection;

    private orderRepository: OrderRepository;
    private configRepository: ConfigRepository;

    constructor(orderRepository: OrderRepository, configRepository: ConfigRepository) {
        this.orderRepository = orderRepository;
        this.configRepository = configRepository;
    }

    @action
    public loaded = (translations: ITranslationsCollection) => {
        this.translations = translations;
        this.orderRepository.loadTranslations(translations);
    };

    @computed get errorMessage(): string {
        const { errorMessageType } = this.orderRepository.orderTracking;

        if (!this.translations) {
            return '';
        }
        const {
            noOrderFoundErrorMessage,
            orderNumberInvalidErrorMessage,
            orderTrackingGenericErrorMessage,
            trackMyOrderIsNotAvailable
        } = this.translations;
        switch (errorMessageType) {
            case ErrorMessageType.ORDER_NOT_FOUND:
                return noOrderFoundErrorMessage;
            case ErrorMessageType.ORDER_NUMBER_INVALID:
                return orderNumberInvalidErrorMessage;
            case ErrorMessageType.GENERIC:
                return orderTrackingGenericErrorMessage;
            case ErrorMessageType.TRACK_MY_ORDER_IS_NOT_AVAILABLE:
                return trackMyOrderIsNotAvailable;
            default:
                return '';
        }
    }

    @computed
    public get data() {
        const { orderInfo, orderTracking } = this.orderRepository;
        const { queryOrderNumber, isLoading } = orderTracking;
        const {
            orderNumberValidationMaxLength,
            isVerticalTMOProgressBarDisplay
        } = this.configRepository.orderTrackingConfig;
        this.isVerticalTMOProgressBar = isVerticalTMOProgressBarDisplay;

        return {
            orderInfo,
            queryOrderNumber,
            errorMessage: this.errorMessage,
            orderNumberValidationMaxLength,
            isVerticalTMOProgressBarDisplay,
            isLoading
        };
    }

    @computed
    public get orderExplainPlaceholderValues(): FormattedTextValues {
        const { orderInfo } = this.orderRepository;
        let orderDateValue = '';
        let shippedDateValue = '';
        let deliveredDateValue = '';
        let cutOffTimeValue = '';
        let carrierNameValue = '';

        if (orderInfo) {
            const { orderDateFormattedString, orderDate, shippingInfo, deliveredDate } = orderInfo;
            orderDateValue = orderDateFormattedString || orderDate;
            deliveredDateValue = deliveredDate;

            if (shippingInfo) {
                const {
                    shippedDateFormattedString,
                    shippedDate,
                    cutOffTime,
                    carrierName
                } = shippingInfo;

                shippedDateValue = shippedDateFormattedString || shippedDate;
                cutOffTimeValue = cutOffTime;
                carrierNameValue = carrierName;
            }
        }

        return {
            ORDER_DATE: orderDateValue,
            SHIPPED_DATE: shippedDateValue,
            DELIVERED_DATE: deliveredDateValue,
            CUTOFF_TIME: cutOffTimeValue,
            CARRIER_NAME: carrierNameValue
        };
    }

    @computed
    public get shouldScroll(): boolean {
        return !this.isScrolled && !!this.orderRepository.orderInfo;
    }

    @action
    public onChangeQueryOrderNumber = (orderNumber: string) => {
        this.orderRepository.orderTracking.setQueryOrderNumber(orderNumber);
    };

    @action
    public onQueryOrder = async (encryptedOrderNumberData?: EncryptedOrderNumberDataType) => {
        // Should mark isScrolled to false before changing other view state because this flag won't trigger view re-render itself.
        this.isScrolled = false;
        const submitUseCase = new OrderTrackingSubmittedUseCase(encryptedOrderNumberData);
        await submitUseCase.execute();
    };

    @action
    public scrollComplete = () => {
        this.isScrolled = true;
    };

    @action
    public hideTrackMyOrderDisplay = () => {
        this.isTrackOrderShow = false;
    };

    public onSignInLinkClick = () => {
        emit(events.SIGN_IN_LINK_CLICKED);
    };
}
